import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  editProfilePicture,
  removeProfilePicture,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { User } from '../../../../types';
import GeneralDialog from '../../../shared/general-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import AddAPhoto from '@mui/icons-material/AddAPhoto';
interface EditProfilePicProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  user: User;
  changeLogRoles: string[];
}

export default function EditProfilePicDialogOld({
  open,
  handleClose,
  userEmail,
  user,
  changeLogRoles,
}: EditProfilePicProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [isUpload, setIsUpload] = useState(false);
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const { mutate, isPending: isEditing } = useMutation({
    mutationFn: () =>
      editProfilePicture(userEmail, uploadedFile!, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User profile picture updated successfully!',
      });
      setChangeLogMessage('');
      const userWithoutPic = {
        ...user,
        thumbnailPhotoUrl: '',
      };
      queryClient.setQueryData([UserKeys.User, userEmail], userWithoutPic);
      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserPhoto, userEmail],
      });
      setIsUpload(false);
      setUploadedFile(null);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue editing the user's profile picture. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_details.update_profile_pic, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.sections.userDetails,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: deleteMutate, isPending: isDeleting } = useMutation({
    mutationFn: () => removeProfilePicture(userEmail, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User profile picture deleted successfully',
      });
      setChangeLogMessage('');
      const userWithoutPic = {
        ...user,
        thumbnailPhotoUrl: null,
      };
      queryClient.setQueryData([UserKeys.User, userEmail], userWithoutPic);
      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserPhoto, userEmail],
      });
      setIsUpload(false);
      setUploadedFile(null);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue deleting the user's profile picture. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_details.delete_profile_pic);
      setOpenChangeLogDialog(false);
    },
  });

  function handleUploadSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.ChangeProfilePicture.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  function handleDeleteSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.ChangeProfilePicture.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      deleteMutate();
    }
  }

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        handleClose();
        setIsUpload(false);
        setUploadedFile(null);
      }}
      title={`Edit Profile Picture`}
      dialogContent={
        <Stack gap={2} alignItems="center">
          <Typography variant="body2">
            Photo update may take a couple of seconds to be displayed.
          </Typography>
          <Stack flexDirection="row" alignItems="center" gap={3}>
            <Typography>
              {uploadedFile !== null ? uploadedFile.name : 'No File Chosen'}
            </Typography>
            <Box width={200}>
              <label htmlFor="upload-photo">
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<AddAPhoto />}
                  disabled={isDeleting || isEditing}
                >
                  <VisuallyHiddenInput
                    id="upload-photo"
                    name="upload-photo"
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      setUploadedFile(
                        event.target.files ? event.target.files[0] : null
                      )
                    }
                  />
                  Upload photo
                </Button>
              </label>
            </Box>
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              if (isUpload) {
                mutate();
              } else {
                deleteMutate();
              }
            }}
            mutateLoading={isEditing || isDeleting}
          />
        </Stack>
      }
      primaryButton={{
        loading: isEditing,
        action: () => {
          setIsUpload(true);
          handleUploadSubmit();
        },
        disabled: uploadedFile === null || isDeleting || isEditing,
        title: 'Add',
      }}
      secondaryButton={{
        loading: isDeleting,
        action: () => {
          setIsUpload(false);
          handleDeleteSubmit();
        },
        disabled: isDeleting || isEditing,
        title: 'Delete',
        shown: user.thumbnailPhotoUrl !== null,
        color: 'error',
      }}
    />
  );
}
